import { ArrowRight, RefreshCw, X } from 'lucide-react';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import CloneScanningView from './CloneScanningView';

const INTENT_LABELS = { match_visual_style: 'Style Match', structure_inspiration: 'Structure', redesign_reference: 'Redesign', create_my_version: 'My Version' };
const STATUS_COLORS = { complete: '#34D399', partial: '#FBBF24', blocked: '#F87171' };
const STATUS_LABELS = { complete: 'Complete', partial: 'Partial', blocked: 'Blocked' };

// CloneBuildStage is the preview pane + progress bar + action bar.
// Agent log / step details are in CloneAgentPanel.

export default function CloneBuildStage({
  previewHtml, previewCss, previewJs, analysis,
  isComplete, isBuilding, error, result, steps, elapsedMs,
  onComplete, onRetry, onCancel,
  cloneIntent, referenceFidelity, analysisStatus,
}) {
  const previewSrcDoc = previewHtml
    ? (previewHtml.includes('<html')
        ? previewHtml
        : `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>${previewCss || ''}</style></head><body>${previewHtml}<script>${previewJs || ''}</script></body></html>`)
    : null;

  // Compute progress from steps
  const totalSteps = steps?.length || 0;
  const doneSteps = steps?.filter(s => s.status === 'done').length || 0;
  const progressPct = totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : 0;
  const currentStep = steps?.find(s => s.status === 'active') || steps?.find(s => s.status === 'pending');
  const elapsedSec = Math.floor((elapsedMs || 0) / 1000);
  const elapsedStr = elapsedSec >= 60 ? `${Math.floor(elapsedSec / 60)}:${String(elapsedSec % 60).padStart(2, '0')}` : `0:${String(elapsedSec).padStart(2, '0')}`;

  return (
    <div className="flex-1 flex flex-col min-h-0" style={{ position: 'relative' }}>
      {/* Status chips */}
      {cloneIntent && (
        <div className="flex items-center gap-2 px-4 py-2 flex-shrink-0" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.06)', background: 'rgba(14,14,14,0.7)' }}>
          <span style={{ fontSize: '11px', fontWeight: 400, padding: '2px 10px', borderRadius: '100px', background: 'rgba(184,204,223,0.08)', border: '0.5px solid rgba(184,204,223,0.15)', color: 'var(--orbis-blue)' }}>
            {INTENT_LABELS[cloneIntent] || cloneIntent}
          </span>
          <span style={{ fontSize: '11px', fontWeight: 400, padding: '2px 10px', borderRadius: '100px', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)', color: '#8A8882' }}>
            Fidelity: {referenceFidelity ?? 72}%
          </span>
          {analysisStatus && (
            <span style={{ fontSize: '11px', fontWeight: 400, padding: '2px 10px', borderRadius: '100px', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)', color: STATUS_COLORS[analysisStatus] || '#8A8882', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: STATUS_COLORS[analysisStatus] || '#8A8882' }} />
              {STATUS_LABELS[analysisStatus] || analysisStatus}
            </span>
          )}
        </div>
      )}

      {/* Preview — full area */}
      <div className="flex-1 overflow-hidden">
        {previewSrcDoc ? (
          <iframe
            srcDoc={previewSrcDoc}
            className="w-full h-full"
            sandbox="allow-scripts allow-same-origin"
            title="Clone Preview"
            style={{ border: 'none', display: 'block' }}
          />
        ) : (
          <CloneScanningView url={analysis?.url} isDone={isComplete} screenshotUrl={(analysis?.screenshot_urls || [])[0] || null} />
        )}
      </div>

      {/* Progress bar — shown during active build */}
      {isBuilding && !isComplete && !error && (
        <div className="flex-shrink-0 px-5 py-3" style={{ borderTop: '0.5px solid rgba(255,255,255,0.07)', background: 'rgba(14,14,14,0.95)' }}>
          {/* Progress track */}
          <div style={{ width: '100%', height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)', marginBottom: 8, overflow: 'hidden' }}>
            <div style={{ width: `${progressPct}%`, height: '100%', borderRadius: 2, background: 'var(--orbis-blue)', transition: 'width 0.5s ease-out' }} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <OrbisSpinner size={12} color="var(--orbis-blue)" />
              <span style={{ fontSize: '12px', fontWeight: 300, color: '#8A8882' }}>
                {currentStep?.label || 'Building...'} — {progressPct}%
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', color: '#5A5855' }}>{elapsedStr}</span>
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="flex items-center gap-1"
                  style={{ height: '28px', padding: '0 10px', borderRadius: '100px', background: 'transparent', border: '0.5px solid rgba(255,255,255,0.1)', fontSize: '11px', color: '#5A5855', cursor: 'pointer' }}
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom action bar — shown when done or error */}
      {(isComplete || error) && (
        <div
          className="flex-shrink-0 flex items-center justify-between px-6 py-4"
          style={{ borderTop: '0.5px solid rgba(255,255,255,0.07)', background: 'rgba(14,14,14,0.95)' }}
        >
          <div>
            {error && (
              <span style={{ fontSize: '13px', color: '#E07070' }}>{error}</span>
            )}
          </div>
          <div className="flex gap-3">
            {error && (
              <button
                onClick={onRetry}
                className="flex items-center gap-2"
                style={{ height: '36px', padding: '0 16px', borderRadius: '100px', background: 'transparent', border: '0.5px solid rgba(255,255,255,0.12)', fontSize: '13px', color: '#8A8882', cursor: 'pointer' }}
              >
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </button>
            )}
            {isComplete && (
              <button
                onClick={() => onComplete(result)}
                className="flex items-center gap-2"
                style={{ height: '44px', padding: '0 24px', borderRadius: '100px', background: 'var(--orbis-blue)', border: 'none', fontSize: '14px', fontWeight: 400, color: 'var(--orbis-bg)', cursor: 'pointer' }}
              >
                Review Website <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
